import React from 'react';
import { Question } from '../types';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

interface TaxonomicModalProps {
  question: Question;
  userSubmission: {
    inputs: string;
    target: string;
    theory: string;
    formula: string;
    failureReason?: string | null;
  };
  onNextQuestion: () => void;
}

export const TaxonomicModal: React.FC<TaxonomicModalProps> = ({
  question,
  userSubmission,
  onNextQuestion,
}) => {
  const isFailed = Boolean(userSubmission.failureReason);

  const getFailureLabel = (code?: string | null) => {
    switch (code) {
      case 'OBSCURED_THEORY':
        return 'Falha de Decodificação (Assunto não identificado)';
      case 'FORGOT_FORMULA':
        return 'Falha de Memória (Fórmula esquecida)';
      case 'CALCULATION_ERROR':
        return 'Falha de Execução (Travou no cálculo / dados)';
      case 'MISREAD_INPUTS':
        return 'Leitura Incorreta de Grandezas';
      default:
        return 'Triagem Concluída com Sucesso';
    }
  };

  return (
    <div className="rounded-lg border border-paper-300 bg-paper-100 p-6 shadow-sm dark:border-study-line dark:bg-study-panel">
      <div className="flex items-center justify-between border-b border-paper-200 pb-4 dark:border-study-line">
        <div className="flex items-center gap-2">
          {isFailed ? (
            <XCircle className="h-5 w-5 text-amber-500 shrink-0" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
          )}
          <span className="font-serif text-base font-bold text-zinc-900 dark:text-zinc-100">
            Gabarito Taxonômico e Diagnóstico
          </span>
        </div>
        <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-paper-200 text-zinc-700 dark:bg-study-card dark:text-zinc-300">
          {question.source} — Q{question.questionNumber}
        </span>
      </div>

      {isFailed && (
        <div className="mt-4 rounded border border-amber-300 bg-amber-50/50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-300">
          <span className="font-semibold">Causa-Raiz Registrada:</span> {getFailureLabel(userSubmission.failureReason)}
        </div>
      )}

      {/* Side-by-side comparison: Student attempt vs Master key */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Student input */}
        <div className="rounded border border-paper-300 bg-paper-50 p-4 dark:border-study-line dark:bg-study-card">
          <h4 className="font-mono text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-3">
            Sua Decodificação
          </h4>
          <div className="space-y-3 text-xs">
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 dark:text-zinc-500">Dados Fornecidos</p>
              <p className="font-mono text-zinc-800 dark:text-zinc-200">
                {userSubmission.inputs || <span className="italic text-zinc-400">(vazio)</span>}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 dark:text-zinc-500">Incógnita Alvo</p>
              <p className="font-mono text-zinc-800 dark:text-zinc-200">
                {userSubmission.target || <span className="italic text-zinc-400">(vazio)</span>}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 dark:text-zinc-500">Gatilho / Teoria</p>
              <p className="font-sans font-medium text-zinc-800 dark:text-zinc-200">
                {userSubmission.theory || <span className="italic text-zinc-400">(vazio)</span>}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 dark:text-zinc-500">Fórmula-Ponte</p>
              <p className="font-mono text-emerald-700 dark:text-emerald-400">
                {userSubmission.formula || <span className="italic text-zinc-400">(vazio)</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Master taxonomic key */}
        <div className="rounded border border-paper-300 bg-paper-50 p-4 dark:border-study-line dark:bg-study-card">
          <h4 className="font-mono text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-3">
            Gabarito Conceitual Esperado
          </h4>
          <div className="space-y-3 text-xs">
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 dark:text-zinc-500">Gatilhos do Enunciado</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {question.triggers.map((t, idx) => (
                  <span
                    key={idx}
                    className="rounded bg-paper-200 px-1.5 py-0.5 font-mono text-[10px] text-zinc-700 dark:bg-study-line dark:text-zinc-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 dark:text-zinc-500">Grandezas Fornecidas</p>
              <p className="font-mono text-zinc-800 dark:text-zinc-200">
                {question.inputs.join('  •  ')}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 dark:text-zinc-500">Teoria / Modelo Subjacente</p>
              <p className="font-medium text-indigo-700 dark:text-indigo-400">
                {question.theory}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 dark:text-zinc-500">Fórmula-Ponte Exata</p>
              <div className="rounded bg-paper-200/70 p-1.5 font-mono text-xs font-semibold text-emerald-800 dark:bg-study-line/80 dark:text-emerald-300">
                <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                  {`$${question.formulaBridge}$`}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Official alternative and explanation */}
      <div className="mt-4 rounded border border-paper-200 bg-paper-50 p-4 dark:border-study-line dark:bg-study-card text-xs">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold text-zinc-900 dark:text-zinc-100">
            Alternativa Correta da Banca: <span className="uppercase text-emerald-600 dark:text-emerald-400 font-mono text-sm">{question.correctAlternative}</span>
          </span>
        </div>
        {question.explanation && (
          <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed font-sans">
            {question.explanation}
          </p>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onNextQuestion}
          className="flex items-center gap-2 rounded bg-zinc-900 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white transition"
        >
          Próxima Questão
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
